import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, PartyPopper, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";

function Frame({ icon: Icon, color, title, message, actions, children }) {
  return (
    <div className="min-h-screen grid place-items-center px-5 pt-24 pb-16 bg-background">
      <div className="text-center max-w-md w-full">
        <div className={`grid place-items-center w-16 h-16 rounded-full mx-auto mb-6 ${color}`}><Icon size={30} /></div>
        <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-4">{message}</p>
        <div className="flex flex-wrap gap-3 justify-center mt-8">{actions}</div>
        {children}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [q] = useSearchParams();
  const { user } = useAuth();
  const oid = q.get("order"); const eid = q.get("enrolment");
  const loginRequired = q.get("login") === "1";
  const [data, setData] = useState(null);
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwDone, setPwDone] = useState(false);

  useEffect(() => {
    if (!eid || loginRequired) return;
    let tries = 0;
    const tick = setInterval(async () => {
      tries += 1;
      try { const { data } = await api.get(`/me/enrolments/${eid}`); if (data.enrolment) { setData(data); clearInterval(tick); } } catch {}
      if (tries >= 20) clearInterval(tick);
    }, 1500);
    return () => clearInterval(tick);
  }, [eid, loginRequired]);

  const setPassword = async () => {
    if (pw.length < 6) { toast.error("At least 6 characters."); return; }
    setPwBusy(true);
    try {
      await api.put("/auth/password", { password: pw });
      setPwDone(true);
      toast.success("Password set — your account is secured.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPwBusy(false); }
  };

  const title = data?.program?.title || "6-Day Bodyweight Fitness Challenge";
  const canAccess = user && !loginRequired;

  return (
    <Frame icon={PartyPopper} color="bg-primary/15 text-primary"
      title="You're In!"
      message={<><span className="text-foreground font-semibold">Your {title} is ready.</span><br />Day 1 is unlocked — a new day opens at midnight (IST).{oid && <><br /><span className="text-xs">Order: {oid.slice(0, 8)}…</span></>}</>}
      actions={canAccess && eid ? [
        <Link key="s" data-testid="start-day1" to={eid ? `/app/programs/${eid}` : "/app/programs"} className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-widest font-bold hover:scale-[1.03] transition-transform">Start Day 1</Link>,
        ...(user.onboarding_complete === false ? [<Link key="p" data-testid="complete-profile" to="/onboarding" className="border border-border px-8 py-3.5 rounded-full text-xs uppercase tracking-widest font-bold">Complete Your Fitness Profile <span className="text-muted-foreground normal-case">(optional)</span></Link>] : []),
      ] : [
        <Link key="l" data-testid="success-login" to="/login" className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-widest font-bold">Log In to Start Day 1</Link>,
      ]}>
      {loginRequired && (
        <p className="text-xs text-muted-foreground mt-4" data-testid="login-required-note">Your enrolment is active. Log in with your purchase email to access it — use Google or your password.</p>
      )}
      {canAccess && user?.needs_password && !pwDone && (
        <div className="mt-8 border border-border rounded-xl bg-card p-5 text-left" data-testid="set-password-card">
          <div className="flex items-center gap-2 font-bold text-sm"><KeyRound size={15} className="text-primary" /> Secure your account <span className="text-[10px] uppercase text-muted-foreground font-normal">(optional)</span></div>
          <p className="text-xs text-muted-foreground mt-1">Set a password so you can log in anytime, on any device.</p>
          <div className="flex gap-2 mt-3">
            <Input type="password" data-testid="set-password-input" placeholder="New password (6+ chars)" value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" />
            <button onClick={setPassword} disabled={pwBusy} data-testid="set-password-btn" className="bg-primary text-primary-foreground px-5 rounded-full text-xs uppercase font-bold shrink-0 disabled:opacity-60">{pwBusy ? "…" : "Set"}</button>
          </div>
        </div>
      )}
    </Frame>
  );
}

export function PaymentFailed() {
  return <Frame icon={XCircle} color="bg-destructive/15 text-destructive"
    title="Payment Not Completed" message="Your payment could not be completed. Your program access has not been activated. Please try again."
    actions={[<Link key="r" to="/programs/6-day-bodyweight-challenge" className="bg-primary text-primary-foreground px-8 py-3 rounded-full text-xs uppercase tracking-widest font-bold">Try Payment Again</Link>,
      <Link key="c" to="/contact" className="border border-border px-8 py-3 rounded-full text-xs uppercase tracking-widest font-bold">Get Help</Link>]} />;
}

export function PaymentPending() {
  return <Frame icon={Clock} color="bg-secondary text-muted-foreground"
    title="Payment Processing" message="Your payment is being processed. Please do not make another payment immediately — we'll update your status shortly."
    actions={[<Link key="m" to="/app/programs" className="border border-border px-8 py-3 rounded-full text-xs uppercase tracking-widest font-bold">Go to My Programs</Link>]} />;
}
